import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardFooter, CardHeader } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Checkbox } from '../components/Field.js'
import { Icon } from '../components/Icon.js'
import { StatusPill } from '../components/StatusPill.js'
import { FeatureGate } from '../components/FeatureGate.js'
import { FEATURES } from '@academy/config'

export const Route = createFileRoute('/_app/settings/branding')({
  component: () => (
    <FeatureGate feature={FEATURES.WHITE_LABEL}>
      <BrandingSettingsPage />
    </FeatureGate>
  ),
})

interface BrandingPayload {
  logoUrl: string | null
  primaryColor: string | null
  accentColor: string | null
  loginMethods: ('email' | 'google')[]
  whiteLabelFromEmail: string | null
  customDomain: string | null
  customDomainVerified: boolean
  customDomainVerificationToken: string | null
  whiteLabelEnabled: boolean
}

interface CustomDomainResponse {
  domain: string
  verified: boolean
  token: string
  txtRecord: { host: string; value: string; ttl: number }
  cnameRecord: { host: string; value: string }
  instructions: string
}

function BrandingSettingsPage() {
  const qc = useQueryClient()

  const branding = useQuery({
    queryKey: ['academy', 'branding'],
    queryFn: async () => {
      const res = await api<BrandingPayload>('/api/v1/academy/branding')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [logoUrl, setLogoUrl] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#0F766E')
  const [accentColor, setAccentColor] = useState('#8B5CF6')
  const [emailMethod, setEmailMethod] = useState(true)
  const [googleMethod, setGoogleMethod] = useState(true)
  const [whiteLabelFromEmail, setWhiteLabelFromEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  // Hydrate form state when API responds
  useEffect(() => {
    if (!branding.data) return
    setLogoUrl(branding.data.logoUrl ?? '')
    setPrimaryColor(branding.data.primaryColor ?? '#0F766E')
    setAccentColor(branding.data.accentColor ?? '#8B5CF6')
    setEmailMethod(branding.data.loginMethods.includes('email'))
    setGoogleMethod(branding.data.loginMethods.includes('google'))
    setWhiteLabelFromEmail(branding.data.whiteLabelFromEmail ?? '')
  }, [branding.data])

  const whiteLabel = branding.data?.whiteLabelEnabled ?? false

  const saveBranding = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        logoUrl: logoUrl || null,
        primaryColor,
        accentColor,
      }
      if (whiteLabel) {
        const methods = [
          ...(emailMethod ? ['email' as const] : []),
          ...(googleMethod ? ['google' as const] : []),
        ]
        if (methods.length === 0) {
          throw new Error('Pick at least one login method')
        }
        payload.loginMethods = methods
        payload.whiteLabelFromEmail = whiteLabelFromEmail || null
      }
      const res = await api('/api/v1/academy', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['academy', 'branding'] })
      qc.invalidateQueries({ queryKey: ['academy'] })
      setSavedAt(new Date().toLocaleTimeString())
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
  })

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Branding & domain"
        description="Customize how the portal looks for your parents, teachers, and students."
      />

      {!whiteLabel ? (
        <div className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-tint px-4 py-3 text-sm text-warning">
          <Icon name="alert" size={16} className="mt-0.5" />
          <p>
            Your current plan covers logo and brand colours. Custom domains, login
            method control, and a white-label sender email require a plan with the{' '}
            <strong>white_label</strong> feature.
          </p>
        </div>
      ) : null}

      <Section title="Look & feel" description="Logo and brand colours.">
        <Card>
          <CardBody className="space-y-4">
            <Field label="Logo URL" hint="Public HTTPS image URL. Square or wide formats both work.">
              <Input
                type="url"
                placeholder="https://cdn.example.com/logo.svg"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Primary colour" hint="Main brand colour for buttons and links.">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-9 w-12 rounded border border-border cursor-pointer"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="font-mono uppercase"
                  />
                </div>
              </Field>
              <Field label="Accent colour" hint="Secondary highlight colour.">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="h-9 w-12 rounded border border-border cursor-pointer"
                  />
                  <Input
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="font-mono uppercase"
                  />
                </div>
              </Field>
            </div>
            {logoUrl ? (
              <div className="flex items-center gap-3 rounded-md border border-border bg-surface-muted px-4 py-3">
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  className="h-10 w-auto rounded bg-surface"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <p className="text-xs text-fg-subtle">Preview</p>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </Section>

      <Section
        title="Login methods"
        description="Pick how parents and teachers sign in. Requires white-label."
      >
        <Card>
          <CardBody className="space-y-3">
            <Checkbox
              label="Email + password"
              checked={emailMethod}
              onChange={setEmailMethod}
              disabled={!whiteLabel}
            />
            <Checkbox
              label="Continue with Google"
              checked={googleMethod}
              onChange={setGoogleMethod}
              disabled={!whiteLabel}
            />
          </CardBody>
        </Card>
      </Section>

      <Section
        title="White-label email sender"
        description="Custom From address for fee receipts, attendance alerts, and exam reminders."
      >
        <Card>
          <CardBody>
            <Field
              label="From address"
              hint="Must use a domain you can verify with our email provider. Leave blank to use the platform default."
            >
              <Input
                type="email"
                placeholder="notifications@yourdomain.com"
                value={whiteLabelFromEmail}
                onChange={(e) => setWhiteLabelFromEmail(e.target.value)}
                disabled={!whiteLabel}
              />
            </Field>
          </CardBody>
        </Card>
      </Section>

      <Card>
        <CardFooter className="flex items-center justify-between">
          <p className="text-xs text-fg-subtle">
            {savedAt ? (
              <span className="inline-flex items-center gap-1 text-success">
                <Icon name="check" size={12} />
                Saved at {savedAt}
              </span>
            ) : (
              'Brand colours apply on every device the next time it loads.'
            )}
            {error ? <span className="ml-2 text-danger">{error}</span> : null}
          </p>
          <Button onClick={() => saveBranding.mutate()} loading={saveBranding.isPending}>
            Save branding
          </Button>
        </CardFooter>
      </Card>

      {whiteLabel ? <CustomDomainSection /> : null}
    </div>
  )
}

function CustomDomainSection() {
  const qc = useQueryClient()
  const [domainInput, setDomainInput] = useState('')
  const [setupResult, setSetupResult] = useState<CustomDomainResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [verified, setVerified] = useState(false)

  const branding = useQuery({
    queryKey: ['academy', 'branding'],
    queryFn: async () => {
      const res = await api<BrandingPayload>('/api/v1/academy/branding')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  useEffect(() => {
    if (!branding.data) return
    if (branding.data.customDomain) setDomainInput(branding.data.customDomain)
    setVerified(branding.data.customDomainVerified)
  }, [branding.data])

  const setDomain = useMutation({
    mutationFn: async () => {
      const res = await api<CustomDomainResponse>('/api/v1/academy/custom-domain', {
        method: 'POST',
        body: JSON.stringify({ domain: domainInput }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      setSetupResult(data)
      setVerified(false)
      setError(null)
      qc.invalidateQueries({ queryKey: ['academy', 'branding'] })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const verifyDomain = useMutation({
    mutationFn: async () => {
      const res = await api<{ verified: boolean; mocked?: boolean }>(
        '/api/v1/academy/custom-domain/verify',
        { method: 'POST', body: JSON.stringify({}) },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      setVerified(data.verified)
      setError(null)
      qc.invalidateQueries({ queryKey: ['academy', 'branding'] })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const removeDomain = useMutation({
    mutationFn: async () => {
      const res = await api('/api/v1/academy/custom-domain', { method: 'DELETE' })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      setSetupResult(null)
      setDomainInput('')
      setVerified(false)
      setError(null)
      qc.invalidateQueries({ queryKey: ['academy', 'branding'] })
    },
  })

  const existing = branding.data?.customDomain
  const existingToken = branding.data?.customDomainVerificationToken

  return (
    <Section
      title="Custom domain"
      description="Point your own domain at the parent portal."
    >
      <Card>
        <CardBody className="space-y-4">
          <Field
            label="Domain"
            hint="Like academy.yourdomain.com — no scheme, no path."
          >
            <Input
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value.trim().toLowerCase())}
              placeholder="academy.yourdomain.com"
            />
          </Field>
          {existing ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-fg-subtle">Current:</span>
              <span className="font-mono text-fg">{existing}</span>
              <StatusPill tone={verified ? 'active' : 'pending'}>
                {verified ? 'Verified' : 'Pending'}
              </StatusPill>
            </div>
          ) : null}
        </CardBody>
        <CardFooter className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-fg-subtle">
            {error ? <span className="text-danger">{error}</span> : null}
          </p>
          <div className="flex items-center gap-2">
            {existing ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Remove ${existing}?`)) removeDomain.mutate()
                }}
              >
                Remove
              </Button>
            ) : null}
            <Button
              onClick={() => setDomain.mutate()}
              loading={setDomain.isPending}
              disabled={!domainInput || domainInput === existing}
              variant="secondary"
            >
              {existing ? 'Update' : 'Save domain'}
            </Button>
            {existing ? (
              <Button
                onClick={() => verifyDomain.mutate()}
                loading={verifyDomain.isPending}
                disabled={verified}
              >
                {verified ? 'Verified' : 'Verify'}
              </Button>
            ) : null}
          </div>
        </CardFooter>
      </Card>

      {(setupResult || (existing && existingToken)) && !verified ? (
        <Card>
          <CardHeader title="DNS records" description="Add these at your DNS provider, then click Verify." />
          <CardBody className="space-y-3">
            <DnsRow
              label="TXT record"
              host={setupResult?.txtRecord.host ?? `_academy.${existing}`}
              value={
                setupResult?.txtRecord.value ?? `academy-verify=${existingToken ?? ''}`
              }
            />
            {setupResult?.cnameRecord ? (
              <DnsRow
                label="CNAME record"
                host={setupResult.cnameRecord.host}
                value={setupResult.cnameRecord.value}
              />
            ) : null}
            <p className="text-xs text-fg-subtle">
              {setupResult?.instructions ??
                'Once the TXT record is published, click Verify above.'}
            </p>
          </CardBody>
        </Card>
      ) : null}
    </Section>
  )
}

function DnsRow({ label, host, value }: { label: string; host: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-muted px-3 py-2 text-xs">
      <p className="text-fg-subtle uppercase tracking-wide">{label}</p>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <span className="text-fg-subtle">Host</span>
        <span className="font-mono text-fg break-all">{host}</span>
        <span className="text-fg-subtle">Value</span>
        <span className="font-mono text-fg break-all">{value}</span>
      </div>
    </div>
  )
}
